import * as React from 'react'
import {ReactElement, useEffect, useRef} from 'react'
import OdxModal from '../../../../OdxComponents/NativeOdx/Modal/Odx-Modal'
import {odxVariants} from '../../../../OdxComponents/constants'
import OdxWizard from '../../../../OdxComponents/NativeOdx/Odx-Wizard'
import {IListStaticModule} from '../../../../BackendFacade/IStaticModules'
import {IModule} from '../../../../BackendFacade/IModuleInstances'
import {GlobalDictionary} from '../../../../Utils/globalDictionary'
import {getListOfAllModules} from '../../../../Utils/modules'
import {CreationProps} from '../../../../HubFramework/IHub'
import ModuleAddCard from '../../../../OdxComponents/CustomOdx/ModuleAddCard'
import {moduleIcons} from '../../Icons/Icons'
import {deepCopy} from '../../../../Utils/transformer'
import OdxInputField from '../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField'
import OdxCheckbox from '../../../../OdxComponents/NativeOdx/FormControls/Odx-Checkbox'
import OdxSelect from '../../../../OdxComponents/NativeOdx/FormControls/Odx-Select'
import OdxChip from '../../../../OdxComponents/NativeOdx/Odx-Chip'
import {transformOdxLayoutRef} from '../../../../OdxComponents/odxLayoutRef'
import {moduleContent as IDMSModule} from '../../../IDMS'
import {moduleContent as IDMSDevModule} from '../../../IDMSDev'
import {createModuleInstance} from '../../BackendFacade/apiCalls'
import {HubPathRouting} from '../../../../HubFramework/pathBuilder'
import {NetworkData} from '../../../../BackendFacade'
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {orderBy} from "../../../../Utils/sorting";
import {simplifyForHtmlId} from "../../../../Utils/transformer";

interface IProps {
    onSuccessFunction?: (response: NetworkData) => void
    closeFunction?: () => void
    isOpen: boolean
    onErrorFunction?: (response: NetworkData) => void
}

const AddModule: React.FC<IProps> = ({onSuccessFunction, closeFunction, isOpen, onErrorFunction}) => {
    const [availableModules, setAvailableModules] = React.useState<IListStaticModule[]>([])
    const [newModule, setNewModule] = React.useState<IModule | undefined>(undefined)
    const [newListElement, setNewListElement] = React.useState('');
    const [values, setValues] = React.useState<string[]>([]);
    const [currentStep, setCurrentStep] = React.useState<number>(0)
    const [passedSteps, setPassedSteps] = React.useState<number[]>([])
    const containerRef = useRef<HTMLDivElement>(null);
    const [displayName, setDisplayName] = React.useState<string>('')
    const [group, setGroup] = React.useState<string>('')
    const [migrateExisting, setMigrateExisting] = React.useState<boolean>(false)
    const [moduleToAdd, setModuleToAdd] = React.useState<IListStaticModule | undefined>(undefined)
    const { setLoading, isLoading, createErrorToast } = useMain();
    const baseId = 'project-add-module';

    const addChip = () => {
        if (newListElement && !values.includes(newListElement)) {
            setValues([...values, newListElement])
            setNewListElement('')
        }
    }

    useEffect(() => {
        transformOdxLayoutRef(containerRef.current);
    }, [containerRef, currentStep, migrateExisting])

    const CreateModuleCreationProps = (creationProps: CreationProps[]): ReactElement => {
        if (creationProps === undefined || newModule?.migrateExisting) {
            return <></>
        }

        const content: ReactElement[] = creationProps.map((prop) => {
            let formControl: ReactElement

            const propId = `${baseId}-creation-${simplifyForHtmlId(prop.name)}`;
            switch (prop.type) {
                case 'string':
                    formControl = (
                            <OdxInputField
                                className={'mt-6'}
                                id={propId}
                                label={prop.displayName}
                                type="text"
                                isRequired={true}
                                value={prop.value as string}
                                onChange={(e) => {
                                    prop.value = e.target.value
                                    setNewModule({...newModule})
                                }}
                                hint={prop.description}
                            />
                    )
                    break
                case 'number':
                    formControl = (
                        <>
                            <OdxInputField
                                className={'mt-6'}
                                type='number'
                                isRequired={true}
                                label={prop.displayName}
                                id={propId}
                                value={prop.value as string}
                                onChange={(e) => {
                                    prop.value = e.target.value
                                    setNewModule({...newModule})
                                }}
                                hint={prop.description}
                            />
                        </>
                    )
                    break
                case 'boolean':
                    formControl = (
                        <OdxCheckbox
                            className={'!mt-10 !mb-10'}
                            label={prop.displayName}
                            id={propId}
                            checked={prop.value as boolean}
                            onChange={(e) => {
                                prop.value = e.target.checked
                                setNewModule({...newModule})
                            }}
                        />
                    )
                    break
                case 'select':
                    formControl = (
                        <OdxSelect
                            className={'mt-6'}
                            label={prop.displayName}
                            id={propId}
                            isRequired={true}
                            options={prop.options.map((option) => {
                                return {value: option, label: option}
                            })}
                            placeholder={prop.displayName}
                            value={prop.value as string}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                prop.value = e.target.value
                                setNewModule({...newModule})
                            }}
                        ></OdxSelect>
                    )
                    break
                case 'string[]':
                    formControl = (
                        <>
                            <div data-odxLayout={'grid'} className={'mt-6'}>
                                <div data-odxLayout={'9'}>
                                    <OdxInputField
                                        isRequired={true}
                                        id={`${propId}-input`}
                                        label={prop.displayName}
                                        value={newListElement}
                                        onChange={(event) => setNewListElement(event.target.value)}
                                        hint={prop.description}
                                    />
                                </div>
                                <div data-odxLayout={'3'}
                                     className={`${prop.description ? "content-center" : "content-end"}`}>
                                    <odx-button
                                        variant="neutral"
                                        size="lg"
                                        onClick={() => {
                                            addChip()
                                        }}
                                        id={`${propId}-add-btn`}
                                    >
                                        Add
                                    </odx-button>
                                </div>
                            </div>
                            <div>
                                <div className='odxGridContainer flex-wrap pt-4' data-odxLayout='flex inline gap-small' id={`${propId}-values`}>
                                    {values.map((value, index) => (
                                        <div key={index}>
                                            <OdxChip
                                                truncateText={true}
                                                content={value}
                                                removable={true}
                                                size='small'
                                                variant={odxVariants.secondary}
                                                onRemove={() => setValues(values.filter((val) => val !== value))}
                                                id={`${propId}-chip-${simplifyForHtmlId(value)}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    {values.length > 0 && (
                                        <odx-button variant="ghost" size="lg" onClick={() => {
                                            setValues([]);
                                            setNewListElement(undefined)
                                        }} id={`${propId}-clear-btn`}>
                                            Clear All
                                        </odx-button>
                                    )}
                                </div>
                                <hr/>
                            </div>
                        </>
                    )
                    break
                case 'subProps': {
                    const subContent = Object.keys(prop.subProps!).map((key) => {
                        return CreateModuleCreationProps(prop.subProps![key]);
                    });
                    return (
                        <div key={prop.displayName} className={'!pt-3'}>
                            <div>{subContent}</div>
                        </div>
                    );
                }
                default:
                    throw new Error("Unsupported type " + prop.name);
            }

            if (prop.type !== 'subProps') {
                return (
                    <div key={prop.displayName}>
                        <div>{formControl}</div>
                    </div>
                )
            }
        })

        return <>{content}</>
    }

    const addModuleShow = () => {

        let allModules = getListOfAllModules()
        let moduleContent = allModules[moduleToAdd.staticModuleId]
        setNewModule({
            staticModuleId: moduleToAdd.staticModuleId,
            moduleInstanceId: '',
            displayName: '',
            group: 'Other',
            linkedId: {
                projectId: ''
            },
            creationProps: moduleContent.moduleContent.creationProps ? deepCopy(moduleContent.moduleContent.creationProps) : undefined,
            migrateExisting: false
        });
    }

    const areAllValuesFilled = (): boolean => {
        if (!newModule) return false;
        let creationProps = newModule.creationProps
        if (creationProps === undefined) {
            return true
        }
        for (let i = 0; i < creationProps.length; i++) {
            if (creationProps[i].type === "subProps") {
                let subProps = creationProps[i].subProps!["value"]
                for (let j = 0; j < subProps.length; j++) {
                    if (subProps[j].value === undefined || subProps[j].value === "") {
                        return false
                    }
                }
                continue
            }
            if (creationProps[i].value === undefined || creationProps[i].value === "") {
                return false
            }
        }
        return true
    }

    const submitNewModuleInstance = () => {
        if (!newModule) return;

        if (newModule.displayName === "" || newModule.group === "" || newModule.linkedId.projectId === "" || (!areAllValuesFilled() && !newModule.migrateExisting)) {
            createErrorToast("Please fill in all required fields.");
            return;
        }

        const newModuleTmp = deepCopy(newModule);
        if (newModuleTmp.staticModuleId === IDMSModule.moduleName || newModuleTmp.staticModuleId === IDMSDevModule.moduleName) {
            newModuleTmp.linkedId.projectId = newModuleTmp.linkedId.projectId.replace(/-/g, "");
            if (!isNaN(parseInt(newModuleTmp.linkedId.projectId.charAt(0)))) {
                newModuleTmp.linkedId.projectId = "a" + newModuleTmp.linkedId.projectId;
            }
        }

        setLoading(true);

        createModuleInstance(HubPathRouting.currentProject.id, newModuleTmp).then(async (response) => {
            setLoading(false);
            if (response.success) {
                onSuccessFunction(response);
                GlobalDictionary.remove(GlobalDictionary.MODULEINSTANCES);
            } else {
                onErrorFunction(response);
            }
        });
    }

    useEffect(() => {
        LoadModules()
    }, [])

    useEffect(() => {
        if (moduleToAdd) {
            addModuleShow();
        }
    }, [moduleToAdd]);

    const LoadModules = async () => {
        const modules = await GlobalDictionary.getStaticModules()
        setAvailableModules(modules)
    }

    const moduleOptions = <div id={`${baseId}-module-list`}>
        {(orderBy(availableModules, x => x.displayName)).map(availableModule => (
            <div key={availableModule.staticModuleId} id={`${baseId}-module-option-${simplifyForHtmlId(availableModule.staticModuleId)}`}>
                <ModuleAddCard
                    title={availableModule.displayName}
                    description={availableModule.description}
                    imageSrc={
                        moduleIcons[availableModule.staticModuleId] ? moduleIcons[availableModule.staticModuleId] : moduleIcons['default']
                    }
                    onClick={() => {
                        setModuleToAdd(availableModule);
                        setPassedSteps([...passedSteps, currentStep]);
                        setCurrentStep(currentStep + 1);
                    }}
                    idPrefix={`${baseId}-module-option-${simplifyForHtmlId(availableModule.staticModuleId)}`}
                />
            </div>
        ))}
    </div>

    const steps = [
        {
            label: 'Module',
            actions: [
                {
                    label: 'Dismiss',
                    action: () => {
                        closeFunction()
                    },
                    variant: odxVariants.secondary,
                    id: "Dismiss",
                    isDisabled: false
                }
            ],
            isActive: currentStep === 0,
            isPassed: passedSteps.includes(0),
            children: moduleOptions,
        },
        {
            label: 'Details',
            actions: [
                {
                    label: 'Dismiss',
                    action: () => {
                        closeFunction()
                    },
                    variant: odxVariants.secondary,
                    id: "Dismiss",
                    isDisabled: false
                },
                {
                    label: 'Previous',
                    action: () => {
                        setCurrentStep(currentStep - 1)
                        setPassedSteps(passedSteps.filter((step) => step < currentStep - 1))
                    },
                    variant: odxVariants.primary,
                    id: "Back",
                    isDisabled: false
                },
                {
                    label: 'Add Module',
                    action: () => {
                        setPassedSteps([...passedSteps, currentStep])
                        submitNewModuleInstance()
                    },
                    variant: odxVariants.primary,
                    id: "AddModuleBtnConfirmationFinal",
                    isDisabled: false
                }
            ],
            isActive: currentStep === 1,
            isPassed: passedSteps.includes(1),
            children: <>
                {moduleToAdd && (
                    <div className={'pb-6'} id={`${baseId}-selected-summary`}>
                        <ModuleAddCard
                            title={moduleToAdd.displayName}
                            description={moduleToAdd.description}
                            imageSrc={
                                moduleIcons[moduleToAdd.staticModuleId] ? moduleIcons[moduleToAdd.staticModuleId] : moduleIcons['default']
                            }
                            idPrefix={`${baseId}-selected-${simplifyForHtmlId(moduleToAdd.staticModuleId)}`}
                        />
                    </div>)}
                <div>
                    <OdxInputField className={'pb-6'} isRequired={true} id={`${baseId}-display-name`} label={'Display name'}
                                   value={newModule?.displayName || ''}
                                   onChange={(e) => {
                                       setNewModule({...newModule, displayName: e.target.value})
                                   }}/>
                    <OdxInputField className={'pb-6'} isRequired={true} id={`${baseId}-group`} label={'Group'}
                                   value={newModule?.group || ''}
                                   onChange={(e) => {
                                       setNewModule({...newModule, group: e.target.value})
                                   }}/>

                    {newModule && (
                        <OdxInputField className={'pb-6'} isRequired={true} id={`${baseId}-linked-project`} label={"Project Id"}
                                       value={newModule?.linkedId?.projectId || ''}
                                       placeholder={moduleToAdd.requiredLinkedIds.projectId || ''}
                                       onChange={(event) => {
                                           setNewModule({
                                               ...newModule,
                                               linkedId: {...newModule.linkedId, projectId: event.target.value}
                                           })
                                       }}/>)}


                    <OdxCheckbox label={'Migrate Existing'} id={`${baseId}-migrate-existing`} checked={newModule?.migrateExisting} onChange={(e) => {
                        setNewModule({...newModule, migrateExisting: e.target.checked})
                    }}/>
                </div>
                {!newModule?.migrateExisting &&
                    <>
                        {CreateModuleCreationProps(newModule?.creationProps)}
                    </>
                }
            </>
        }
    ]

    const mainContent = () => {
        return (
            <div>
                <OdxModal
                    size={'small'}
                    title={'Add module'}
                    onClose={() => {
                        closeFunction();
                    }}
                    footerButtons={steps[currentStep].actions.map((action) => {
                        const resolveVariant = (variant: odxVariants): string => {
                            switch (variant) {
                        case odxVariants.secondary:
                            return "ghost";
                                case odxVariants.ghost:
                                    return "ghost";
                                case odxVariants.highlight:
                                    return "accent";
                                case odxVariants.success:
                                case odxVariants.confirmation:
                                    return "success";
                                case odxVariants.danger:
                                    return "danger";
                                case odxVariants.warning:
                                    return "warning";
                                case odxVariants.transparent:
                                    return "ghost";
                                case odxVariants.primary:
                                default:
                                    return "primary";
                            }
                        };

                        return (
                            <odx-button
                                disabled={action.isDisabled ? true : undefined}
                                id={`${baseId}-footer-${action.id}`}
                                onClick={action.action}
                                variant={resolveVariant(action.variant)}
                                size="lg"
                            >
                                {action.label}
                            </odx-button>
                        )
                    })}
                    isOpen={isOpen}
                    variant={'sidesheet'}
                    id={baseId}
                >
                    <OdxWizard variant={'horizontal'} steps={steps} className={'!mb-12'} id={`${baseId}-wizard`}/>
                    <>
                        {steps[currentStep].children}
                    </>
                    <odx-separator style={{margin: '1rem 0'}} />
                </OdxModal>
            </div>
        )
    }

    return <div ref={containerRef} id={`${baseId}-view`}>
        {mainContent()}
    </div>
}

export default AddModule
